/**
 * AuditLogPage — `/security/audit-log`.
 *
 * Slice 3: wired to `GET /v1/audit/recent` (AuditRecentController, B3) for
 * the live log table, in addition to the existing F-137 chain-integrity and
 * F-138 archive panels.
 *
 * Filter note: `GET /v1/audit/recent` supports actor, module, since, until.
 * The action/entity filters are forwarded as-is when non-empty (the server
 * ignores unknown query params gracefully). Full filterable table requires
 * a dedicated index on the `module` + `action` columns — Phase 5+ task.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, RefreshCw, ShieldCheck } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  type AuditLogEntry,
  type IntegrityReport,
  listRecentAuditLogs,
  runArchivePass,
  verifyAllChains,
  verifyChain,
} from '../../lib/api/audit';
import { downloadBlob, escapeCsvCell } from '../../lib/csv';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

/** Convert an array of audit-log rows to a CSV string. Quoting per RFC 4180. */
function logsToCsv(rows: readonly AuditLogEntry[]): string {
  const cols = [
    'id',
    'receivedAt',
    'emittedAt',
    'module',
    'action',
    'ceType',
    'actorUserId',
    'actorOperatorId',
    'entityType',
    'entityId',
    'chainId',
    'chainSeq',
    'correlationId',
    'tenantId',
  ] as const;
  const header = cols.join(',');
  const body = rows
    .map((r) => cols.map((c) => escapeCsvCell(r[c as keyof AuditLogEntry])).join(','))
    .join('\n');
  return `${header}\n${body}\n`;
}

function downloadCsv(filename: string, content: string): void {
  downloadBlob(filename, content, 'text/csv;charset=utf-8;');
}

interface FilterState {
  actor: string;
  module: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: FilterState = { actor: '', module: '', from: '', to: '' };

const MODULES = [
  '', 'Mail', 'Delivery', 'Customer', 'Wallet', 'Payment', 'Kyc', 'Franchise',
  'Government', 'Security', 'Identity',
] as const;

export function AuditLogPage(): JSX.Element {
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [chainId, setChainId] = useState('');
  const [chainResult, setChainResult] = useState<IntegrityReport | null>(null);
  const [chainErr, setChainErr] = useState<string | null>(null);

  // Derive query args from filters (skip empty values)
  const queryArgs = {
    limit: 100,
    actor: filters.actor.trim() || undefined,
    module: filters.module || undefined,
    since: filters.from || undefined,
    until: filters.to || undefined,
  };

  const recentLogs = useQuery({
    queryKey: ['audit', 'recent', queryArgs],
    queryFn: () => listRecentAuditLogs(queryArgs),
    retry: false,
  });

  const allChains = useQuery({
    queryKey: ['audit', 'integrity', 'all', 'page'],
    queryFn: () => verifyAllChains(),
    retry: false,
  });

  const verifyOne = useMutation({
    mutationFn: () => verifyChain(chainId.trim()),
    onSuccess: (r) => {
      setChainErr(null);
      setChainResult(r);
    },
    onError: (e: Error) => {
      setChainResult(null);
      setChainErr(e.message);
    },
  });

  const archive = useMutation({
    mutationFn: (dryRun: boolean) => runArchivePass({ dryRun }),
  });

  const allDenied =
    allChains.isError && allChains.error instanceof ApiError &&
    (allChains.error.status === 401 || allChains.error.status === 403);

  const logsDenied =
    recentLogs.isError && recentLogs.error instanceof ApiError &&
    (recentLogs.error.status === 401 || recentLogs.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit log"
        description="Cross-service audit trail and hash-chain integrity."
        actions={
          <button
            type="button"
            data-testid="export-csv"
            disabled={(recentLogs.data?.length ?? 0) === 0}
            title="Export the currently-displayed audit rows to CSV"
            onClick={() => {
              const rows = recentLogs.data ?? [];
              if (rows.length === 0) return;
              const stamp = new Date().toISOString().replace(/[:.]/g, '-');
              downloadCsv(`upmp-audit-log-${stamp}.csv`, logsToCsv(rows));
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        }
      />

      {/* Recent audit log table */}
      <div className="bg-white border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Recent audit events</h3>
          <button
            type="button"
            onClick={() => void recentLogs.refetch()}
            disabled={recentLogs.isFetching}
            className="text-xs text-nipost-green hover:underline flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${recentLogs.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <FilterBar
          searchValue={filters.actor}
          onSearchChange={(v) => setFilters((f) => ({ ...f, actor: v }))}
          searchPlaceholder="Filter by actor user id…"
        >
          <select
            value={filters.module}
            onChange={(e) => setFilters((f) => ({ ...f, module: e.target.value }))}
            className="px-3 py-2 border border-border rounded-lg text-sm"
            aria-label="Module filter"
          >
            <option value="">All modules</option>
            {MODULES.filter((m) => m).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </FilterBar>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">From</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">To</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => setFilters(EMPTY_FILTERS)}
            className="px-3 py-2 text-sm font-medium text-muted border border-border rounded-lg hover:bg-gray-50"
          >
            Clear
          </button>
        </div>

        {logsDenied ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <code className="font-mono">AuditLog/read</code> permission required
            (AUDITOR / SYSTEM_ADMIN).
          </div>
        ) : recentLogs.isError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            Audit service temporarily unavailable: {(recentLogs.error as Error).message}
          </div>
        ) : (
          <div
            data-testid="audit-log-table"
            className="overflow-hidden border border-border rounded-lg"
          >
            {recentLogs.isPending ? (
              <p className="px-4 py-6 text-center text-sm text-muted">Loading audit log…</p>
            ) : (recentLogs.data?.length ?? 0) === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted">No audit events found.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Time</th>
                    <th className="text-left px-4 py-2">Module</th>
                    <th className="text-left px-4 py-2">Action</th>
                    <th className="text-left px-4 py-2">Actor</th>
                    <th className="text-left px-4 py-2">Entity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(recentLogs.data ?? []).map((entry: AuditLogEntry) => (
                    <tr
                      key={entry.id}
                      data-testid={`audit-row-${entry.id}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-2 text-xs font-mono text-muted whitespace-nowrap">
                        {new Date(entry.receivedAt).toLocaleString('en-GB', {
                          month: 'short',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {entry.module ?? <span className="text-muted">—</span>}
                      </td>
                      <td className="px-4 py-2 text-xs font-mono">
                        {entry.action ?? entry.ceType}
                      </td>
                      <td className="px-4 py-2 text-xs font-mono text-muted truncate max-w-[140px]">
                        {entry.actorUserId ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted">
                        {entry.entityType ? `${entry.entityType}/${entry.entityId ?? '?'}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Hash-chain integrity strip */}
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-nipost-green" />
              Hash-chain integrity
            </h3>
            <p className="text-xs text-muted">
              Verifies every audit-log chain has not been tampered with.
            </p>
          </div>
        </div>

        {allChains.isPending ? (
          <p className="text-sm text-muted">Verifying every chain…</p>
        ) : allDenied ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <code className="font-mono">AuditIntegrity/verify</code> permission required
            (AUDITOR / SYSTEM_ADMIN).
          </div>
        ) : allChains.isError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            Audit service temporarily unavailable: {(allChains.error as Error).message}
          </div>
        ) : (allChains.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-muted">No audit chains found.</p>
        ) : (
          <div className="overflow-hidden border border-border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Chain</th>
                  <th className="text-right px-4 py-2">Rows</th>
                  <th className="text-right px-4 py-2">Duration</th>
                  <th className="text-right px-4 py-2">Breaks</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {allChains.data?.map((r) => (
                  <tr key={r.chainId} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-900">{r.chainId}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">
                      {r.totalRows.toLocaleString('en-US')}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs text-muted">
                      {r.durationMs} ms
                    </td>
                    <td className={`px-4 py-2 text-right font-mono text-xs ${
                      r.breaks.length === 0 ? 'text-muted' : 'text-red-600 font-bold'
                    }`}>
                      {r.breaks.length}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={r.ok ? 'VERIFIED' : 'FAILED'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-border rounded-xl p-5 space-y-3">
        <h3 className="font-semibold text-gray-900">Verify chain by id</h3>
        <p className="text-xs text-muted">
          Spot-check a single chain end-to-end.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <label className="block text-xs font-medium text-muted mb-1">Chain id</label>
            <input
              type="text"
              value={chainId}
              onChange={(e) => setChainId(e.target.value)}
              placeholder="e.g. mail.MailItem"
              aria-label="Chain id"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
            />
          </div>
          <button
            type="button"
            onClick={() => verifyOne.mutate()}
            disabled={chainId.trim().length === 0 || verifyOne.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {verifyOne.isPending ? 'Verifying…' : 'Verify chain'}
          </button>
        </div>

        {chainErr ? <p className="text-sm text-danger">{chainErr}</p> : null}
        {chainResult ? (
          <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
            <p>
              <span className="text-xs uppercase tracking-wider text-muted mr-2">Chain</span>
              <span className="font-mono">{chainResult.chainId}</span>
            </p>
            <p>
              <span className="text-xs uppercase tracking-wider text-muted mr-2">Rows</span>
              <span className="font-mono">{chainResult.totalRows}</span>
            </p>
            <p>
              <span className="text-xs uppercase tracking-wider text-muted mr-2">Status</span>
              <StatusBadge status={chainResult.ok ? 'VERIFIED' : 'FAILED'} />
            </p>
          </div>
        ) : null}
      </div>

      <div className="bg-white border border-border rounded-xl p-5 space-y-3">
        <h3 className="font-semibold text-gray-900">Retention archive</h3>
        <p className="text-xs text-muted">
          On-demand archival pass. SYSTEM_ADMIN only.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => archive.mutate(true)}
            disabled={archive.isPending}
            className="px-3 py-2 text-sm font-semibold text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light disabled:opacity-50"
          >
            {archive.isPending && archive.variables === true ? 'Running…' : 'Run dry run'}
          </button>
          <button
            type="button"
            onClick={() => archive.mutate(false)}
            disabled={archive.isPending}
            className="px-3 py-2 text-sm font-semibold text-white bg-nipost-green rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {archive.isPending && archive.variables === false ? 'Running…' : 'Archive now'}
          </button>
        </div>
        {archive.isSuccess ? (
          <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-0.5">
            <p><span className="text-muted mr-2">Processed</span><span className="font-mono">{archive.data.processed}</span></p>
            <p><span className="text-muted mr-2">Archived</span><span className="font-mono">{archive.data.archived}</span></p>
            <p><span className="text-muted mr-2">Skipped</span><span className="font-mono">{archive.data.skipped}</span></p>
            <p><span className="text-muted mr-2">Errors</span><span className={`font-mono ${archive.data.errors.length === 0 ? '' : 'text-red-600'}`}>{archive.data.errors.length}</span></p>
            <p><span className="text-muted mr-2">Mode</span><span className="font-mono">{archive.data.dryRun ? 'dry run' : 'live'}</span></p>
          </div>
        ) : null}
        {archive.isError ? (
          <p className="text-sm text-danger">{(archive.error as Error).message}</p>
        ) : null}
      </div>
    </div>
  );
}
